import {invokeAppSync}     from "../libs/authLib";
import {getApiPrefix}      from "../libs/apiLib";
import config              from "../config";
import {IUserProfile}      from "../interfaces/User/IUserProfile";
import {IUserInput}        from "../interfaces/User/IUserInput";
import {ITrackerInput}     from "../interfaces/User/ITrackerInput";
import {BASE_DISPLAY_UNIT} from "../constants/global";

const baseAppSyncUrl = () => getApiPrefix();

const checkAppSyncUrlExists = () => {
  return !!config.appSync["VRS_APPSYNC_USER_URL"];
};

class VrsUserApi {
  static getLanguageItemsAppSync(cultureName: string, languageCode: string) {
    if (!checkAppSyncUrlExists()) {
      return Promise.resolve([]);
    }

    const graphQLBody = {
      query: `
          query LanguageItemsQuery {
            languageItems(cultureName: "${cultureName}", languageCode: "${languageCode}") {
              Value
              Identifier
            }
          }
        `,
    };

    return invokeAppSync({
      gatewayName: "VRS_APPSYNC_SYSTEM",
      path: baseAppSyncUrl(),
      method: "POST",
      body: graphQLBody,
    }).then((results) => {
      if (results.data?.languageItems) {
        return results.data.languageItems.map((el: { Value: string; Identifier: string; }) => ({
          v: el.Value,
          i: el.Identifier?.trim() || el.Identifier,
        }));
      }
      return [];
    });
  }

  static getUserProfileAppSync(includeUserAlerts = false) {
    if (!checkAppSyncUrlExists()) {
      return Promise.resolve([]);
    }


    const userAlerts = includeUserAlerts
      ? `
    UserAlerts {
      AccessLevelName
      Description
      EventClassName
      EventCodeName
      ModelName
      PlantName
    }
    `
      : "";

    const graphQLBody = {
      query: `
      query ProfileQuery {
        profile {
          Country
          EmailAddress
          FirstName
          LanguageId
          NotificationEmail
          Languages {
            EnglishName
            IetflanguageTag
            IsDefaultForLanguageCode
            LanguageCode
            LanguageId
            LocalName
          }
          LastName
          PhoneNumber
          UserId
          AccessLevel
          AccessLevelId
          PlantIDs
          DisplayUnit
          Prefer24HourTimeFormat
          WorkSchedules {
            EnableNotification
            Schedule {
              Day
              ShiftHours
              ShiftMins
              StartHour
              StartMinute
            }
          }
          ServiceOutageStatus {
            Color
            IsOutageOn
            OutageEndDate
            OutageId
            OutageStartDate
            OutageText
          }
          ListMember {
            Id
            MemberType
          }
          DesignOnly
          ${userAlerts}
        }
      }
        `,
    };

    return invokeAppSync({
      gatewayName: "VRS_APPSYNC_USER",
      path: baseAppSyncUrl(),
      method: "POST",
      body: graphQLBody,
    }).then((results) => {
      if (results.data?.profile) {
        return [{...results.data?.profile, DisplayUnit: results.data?.profile.DisplayUnit || BASE_DISPLAY_UNIT}];
      }
      return [];
    });
  }

  static getUserRegionsAppSync() {
    if (!checkAppSyncUrlExists()) {
      return Promise.resolve([]);
    }

    const graphQLBody = {
      query: `
      query ServiceRegionQuery {
        profile {
          ServiceRegions {
            Id
          }
        }
      }
        `,
    };

    return invokeAppSync({
      gatewayName: "VRS_APPSYNC_USER",
      path: baseAppSyncUrl(),
      method: "POST",
      body: graphQLBody,
    }).then((results) => {
      if (results.data?.profile) {
        return [results.data?.profile];
      }
      return [];
    });
  }

  static saveUserProfileAppSync(
    profileInput: Partial<IUserProfile>,
    includeUserAlerts: boolean
  ) {
    if (!checkAppSyncUrlExists()) {
      return Promise.resolve({
        error: null,
        data: null,
      });
    }

    const userAlerts = includeUserAlerts
      ? `
  UserAlerts {
    AccessLevelName
    Description
    EventClassName
    EventCodeName
    ModelName
    PlantName
  }
  `
      : "";


    // Build the profileInput section dynamically
    const profileFields: string[] = [];

    // Handle special types and custom objects
    for (const [key, value] of Object.entries(profileInput)) {
      // Skip work schedules as it needs special handling
      if (key === 'WorkSchedules') {
        continue;
      }

      // Handle string values (need quotes)
      if (typeof value === 'string') {
        profileFields.push(`${key}: "${value || ''}"`);
      }
      // Handle boolean values (no quotes)
      else if (typeof value === 'boolean') {
        profileFields.push(`${key}: ${value}`);
      }
      // Handle numbers (no quotes)
      else if (typeof value === 'number') {
        profileFields.push(`${key}: ${value}`);
      }
      // Handle null/undefined values
      else if (value === null || value === undefined) {
        profileFields.push(`${key}: ""`);
      }
    }

    // Handle WorkSchedules separately since it's a complex object
    if(profileInput.WorkSchedules) {
      const workSchedulesData = `WorkScheduleData: {
    Schedule: [${profileInput.WorkSchedules.Schedule.map((el) => {
        return `{Day: ${el.Day},
      StartHour: ${el.StartHour},
      StartMinute: ${el.StartMinute},
      ShiftHours: ${el.ShiftHours},
      ShiftMins: ${el.ShiftMins}}`;
      })}],
    EnableNotification: ${profileInput.WorkSchedules.EnableNotification},
    TimeZoneId: 1
  }`;

      profileFields.push(workSchedulesData);
    }

    const graphQLBody = {
      query: `
    mutation SaveProfileMutation {
      saveProfile(profileInput: {
        ${profileFields.join(',\n        ')}
      }) {
        Country
        EmailAddress
        FirstName
        LanguageId
        NotificationEmail
        Languages {
          EnglishName
          IetflanguageTag
          IsDefaultForLanguageCode
          LanguageCode
          LanguageId
          LocalName
        }
        LastName
        PhoneNumber
        UserId
        AccessLevel
        AccessLevelId
        Prefer24HourTimeFormat
        DisplayUnit
        PlantIDs
        WorkSchedules {
          EnableNotification
          Schedule {
            Day
            ShiftHours
            ShiftMins
            StartHour
            StartMinute
          }
        }
        ${userAlerts}
        DesignOnly
      }
    }
      `,
    };

    return invokeAppSync({
      gatewayName: "VRS_APPSYNC_USER",
      path: baseAppSyncUrl(),
      method: "POST",
      body: graphQLBody,
    }).then((results) => {
      if (results.data?.saveProfile) {
        return {
          error: null,
          data: results.data?.saveProfile,
        };
      }

      return {
        error:
          results.errors && results.errors.length > 0
            ? results.errors[0]
            : results.errors,
        data: null,
      };
    });
  }

  static getCountriesAppSync() {
    if (!checkAppSyncUrlExists()) {
      return Promise.resolve([]);
    }

    const graphQLBody = {
      query: `
      query CountriesQuery {
        countries {
          CountryId
          Name
        }
      }
        `,
    };

    return invokeAppSync({
      gatewayName: "VRS_APPSYNC_USER",
      path: baseAppSyncUrl(),
      method: "POST",
      body: graphQLBody,
    }).then((results) => {
      if (results.data?.countries) {
        return results.data?.countries;
      }
      return [];
    });
  }

  static getLanguagesAppSync() {
    if (!checkAppSyncUrlExists()) {
      return Promise.resolve([]);
    }

    const graphQLBody = {
      query: `
      query LanguagesQuery{
        languages {
          EnglishName
          IETFLanguageTag
          LanguageID
          LocalName
        }
      }
        `,
    };

    return invokeAppSync({
      gatewayName: "VRS_APPSYNC_SYSTEM",
      path: baseAppSyncUrl(),
      method: "POST",
      body: graphQLBody,
    }).then((results) => {
      if (results.data?.languages) {
        return results.data?.languages;
      }
      return [];
    });
  }

  static getAccessLevelsAppSync() {
    if (!checkAppSyncUrlExists()) {
      return Promise.resolve([]);
    }

    const graphQLBody = {
      query: `
      query AccessLevelsQuery {
        accessLevels {
          Name
          Value
          External
        }
      }
        `,
    };

    return invokeAppSync({
      gatewayName: "VRS_APPSYNC_USER",
      path: baseAppSyncUrl(),
      method: "POST",
      body: graphQLBody,
    }).then((results) => {
      if (results.data?.accessLevels) {
        return results.data?.accessLevels;
      }
      return [];
    });
  }

  static getCompanyUsersAppSync(companyId: string, fields: string[] = [
    "UserId",
    "EmailAddress"
  ]) {
    if (!checkAppSyncUrlExists()) {
      return Promise.resolve([]);
    }

    const selectedFields = fields.join("\n");

    const graphQLBody = {
      query: `
    query CompanyUsersQuery {
      companyUsers(companyId: "${companyId}") {
        ${selectedFields}
      }
    }
      `,
    };

    return invokeAppSync({
      gatewayName: "VRS_APPSYNC_USER",
      path: baseAppSyncUrl(),
      method: "POST",
      body: graphQLBody,
    }).then((results) => {
      if (results.data?.companyUsers) {
        return results.data?.companyUsers;
      }
      return [];
    });
  }

  static getUsersAppSync(companyId: string, allUsers: boolean, fields: string[] = [
    "UserId",
    "EmailAddress",
    "NotificationEmail",
    "FirstName",
    "LastName",
    "CompanyId",
    "PlantIDs",
    "CountryName",
    "AccessLevelName",
    "AccessLevelId",
    "Disabled",
    "IsDeleted",
    "LanguageId",
    "CountryId",
    "EnableNotification",
    "UserNotes",
    "IsCountryUser"
  ]) {
    if (!checkAppSyncUrlExists()) {
      return Promise.resolve([]);
    }

    const selectedFields = fields.join("\n");

    const graphQLBody = {
      query: `
    query UsersQuery {
      users(companyId: "${companyId === "0" ? "" : companyId}", allUsers: ${allUsers}) {
        ${selectedFields}
      }
    }
      `,
    };

    return invokeAppSync({
      gatewayName: "VRS_APPSYNC_USER",
      path: baseAppSyncUrl(),
      method: "POST",
      body: graphQLBody,
    }).then((results) => {
      if (results.data?.users) {
        return results.data?.users;
      }
      return [];
    });
  }
  static saveUserAppSync(userInput: IUserInput) {
    if (!checkAppSyncUrlExists()) {
      return Promise.resolve({
        error: null,
        data: null,
      });
    }

    const graphQLBody = {
      query: ` mutation SaveUserMutation
      {
        saveUser(userInput: {
          UserId: "${userInput.UserId}"
          FirstName: "${userInput.FirstName}"
          LastName: "${userInput.LastName}"
          EmailAddress: "${userInput.EmailAddress}"
          NotificationEmail: "${userInput.NotificationEmail}"
          EnableNotification: "${userInput.EnableNotification}"
          PhoneNumber: "${userInput.PhoneNumber}"
          UserNotes: "${userInput.UserNotes}"
          Password: "${userInput.Password}"
          ConfirmPassword: "${userInput.ConfirmPassword}"
          AccessLevelId: "${userInput.AccessLevelId}"
          PlantId: ${userInput.PlantId}
          CompanyId: "${userInput.CompanyId}"
          LanguageId: ${userInput.LanguageId}
          CountryId: ${userInput.CountryId}
          Disabled: "${userInput.Disabled}"
          IsCountryUser: "${userInput.IsCountryUser}"
          DeleteUser: "${userInput.DeleteUser}"
          RestoreDeletedUser: "${userInput.RestoreDeletedUser}"
          ExtraOptions: "${userInput.ExtraOptions}"
          DoesDeleteCompletely: "${userInput.DoesDeleteCompletely}"
          PlantIDs: "${userInput.PlantIDs}"
        }) {
            ExtraOptions
            IsAccessLevelChanged
            UserChangedOwnEmail
            Action
            User {
              UserId
              EmailAddress
              NotificationEmail
              FirstName
              LastName
              PlantIDs
              CountryName
              CountryId
              CompanyId
              AccessLevelName
              AccessLevelId
              Disabled
              IsDeleted
              EnableNotification
              LanguageId
              UserNotes
              IsCountryUser
            }
          }
        }
        `,
    };

    return invokeAppSync({
      gatewayName: "VRS_APPSYNC_USER",
      path: baseAppSyncUrl(),
      method: "POST",
      body: graphQLBody,
    }).then((results) => {
      if (results.data?.saveUser) {
        return {
          error: null,
          data: results.data?.saveUser,
        };
      }

      return {
        error:
          results.errors && results.errors.length > 0
            ? results.errors[0]
            : results.errors,
        data: null,
      };
    });
  }

  static saveTrackerAppSync(trackerInput: ITrackerInput) {
    if (!checkAppSyncUrlExists()) {
      return Promise.resolve({
        error: null,
        data: null,
      });
    }

    const graphQLBody = {
      query: `
        mutation SaveTrackerMutation($input: TrackerInput!) {
          saveTracker(trackerInput: $input)
        }
      `,
      variables: {
        input: {
          CompanyId: trackerInput.CompanyId,
          PlantId: trackerInput.PlantId,
          Url: trackerInput.Url,
          ActionText: trackerInput.ActionText,
          Attributes: trackerInput.Attributes ?? "",
          UserId: trackerInput.UserId ?? "",
        },
      },
    };

    return invokeAppSync({
      gatewayName: "VRS_APPSYNC_USER",
      path: baseAppSyncUrl(),
      method: "POST",
      body: graphQLBody,
    }).then((results) => {
      if (results.data?.saveTracker) {
        return {
          error: null,
          data: results.data?.saveTracker,
        };
      }

      return {
        error:
          results.errors && results.errors.length > 0
            ? results.errors[0]
            : results.errors,
        data: null,
      };
    });
  }

  static saveSalesforceTrackerAppSync(
    TrafficSource: string,
    TrafficID: string,
    SFDCRecord: string,
    accessType: string
  ) {
    if (!checkAppSyncUrlExists()) {
      return Promise.resolve({
        error: null,
        data: null,
      });
    }

    const graphQLBody = {
      query: ` mutation SalesforceTrackerMutation
      {
        saveSalesforceTracker(
          TrafficSource: "${TrafficSource}",
          TrafficID: "${TrafficID}",
          SFDCRecord: "${SFDCRecord}",
          AccessType: "${accessType}")
      }
        `,
    };

    return invokeAppSync({
      gatewayName: "VRS_APPSYNC_USER",
      path: baseAppSyncUrl(),
      method: "POST",
      body: graphQLBody,
    }).then((results) => {
      if (results.data?.saveSalesforceTracker) {
        return {
          error: null,
          data: results.data?.saveSalesforceTracker,
        };
      }

      return {
        error:
          results.errors && results.errors.length > 0
            ? results.errors[0]
            : results.errors,
        data: null,
      };
    });
  }

  static async getUserTermsAcceptance(language: string, userId: string) {
    if (!checkAppSyncUrlExists()) {
      return Promise.resolve(null);
    }

    const graphQLBody = {
      query: `
      query UserTermsAcceptanceQuery {
        userTermsAcceptance(language: "${language}", userId: "${userId}") {
          acceptedDate
          content
          language
          userId
          version
        }
      }
    `,
    };

    const results = await invokeAppSync({
      gatewayName: "VRS_APPSYNC_USER",
      path: baseAppSyncUrl(),
      method: "POST",
      body: graphQLBody,
    });
    if (results.data?.userTermsAcceptance) {
      return results.data.userTermsAcceptance;
    }
    return null;
  }

  static async acceptTerms(language: string, userId: string, version: string) {
    if (!checkAppSyncUrlExists()) {
      return {
        error: null,
        data: null,
      };
    }

    const graphQLBody = {
      query: `
      mutation AcceptTermsMutation {
        acceptTerms(userId: "${userId}", version: "${version}", language: "${language}") {
          acceptedDate
          content
          language
          userId
          version
        }
      }
    `,
    };

    const results = await invokeAppSync({
      gatewayName: "VRS_APPSYNC_USER",
      path: baseAppSyncUrl(),
      method: "POST",
      body: graphQLBody,
    });

    if (results.data?.acceptTerms) {
      return {
        error: null,
        data: results.data.acceptTerms,
      };
    }

    return {
      error: results.errors && results.errors.length > 0
        ? results.errors[0]
        : results.errors,
      data: null,
    };
  }
}

export default VrsUserApi;
